<template>
  <div class="admin-dashboard-view">
    <!-- Hero -->
    <section class="dashboard-hero mb-4">
      <div
        class="d-flex flex-column flex-xl-row justify-content-between align-items-xl-center gap-3"
      >
        <div>
          <div class="hero-eyebrow">Admin Dashboard</div>
          <h2 class="hero-title mb-1">GSVG Control Center</h2>

          <div class="mt-3 d-flex flex-wrap gap-2 align-items-center">
            <span class="hero-pill">
              <i class="bi bi-calendar-range me-1"></i>
              {{ niceDate(filters.from) }} → {{ niceDate(filters.to) }}
            </span>

            <span v-if="loading" class="hero-pill">
              <i class="bi bi-hourglass-split me-1"></i>
              Loading...
            </span>
          </div>
        </div>

        <div class="hero-filter-card">
          <div class="row g-2 align-items-end">
            <div class="col-12 col-sm-5">
              <label class="form-label small text-muted mb-1">From</label>
              <input
                ref="fromInput"
                type="text"
                class="form-control"
                placeholder="Select date"
                readonly
              />
            </div>

            <div class="col-12 col-sm-5">
              <label class="form-label small text-muted mb-1">To</label>
              <input
                ref="toInput"
                type="text"
                class="form-control"
                placeholder="Select date"
                readonly
              />
            </div>

            <div class="col-12 col-sm-2 d-grid">
              <button class="btn btn-primary" @click="load" :disabled="loading">
                <i class="bi bi-arrow-repeat me-1"></i>
                {{ loading ? "Refreshing..." : "Refresh" }}
              </button>
            </div>
          </div>

          <div class="d-flex flex-wrap gap-2 mt-3">
            <button
              class="btn btn-light quick-btn"
              @click="setThisYear"
              :disabled="loading"
            >
              <i class="bi bi-calendar2-week me-1"></i>This Year
            </button>

            <button
              class="btn btn-light quick-btn"
              @click="setLast30Days"
              :disabled="loading"
            >
              <i class="bi bi-clock-history me-1"></i>Last 30 Days
            </button>
          </div>
        </div>
      </div>
    </section>

    <div v-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <!-- Top KPI cards -->
    <div class="row g-3 mb-4">
      <div class="col-md-6 col-xl-3">
        <div class="kpi-card kpi-card--blue h-100">
          <div class="d-flex justify-content-between align-items-start gap-3">
            <div>
              <div class="kpi-label">Total Members</div>
              <div class="kpi-value">{{ fmt(kpis.members.total) }}</div>
              <div class="kpi-note">Registered member accounts</div>
            </div>
            <div class="kpi-icon soft-blue">
              <i class="bi bi-people"></i>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6 col-xl-3">
        <div class="kpi-card kpi-card--indigo h-100">
          <div class="d-flex justify-content-between align-items-start gap-3">
            <div>
              <div class="kpi-label">System Net Balance</div>
              <div class="kpi-value">
                {{ currency(kpis.balances.system_net_balance) }}
              </div>
              <div class="kpi-note">Ledger credits minus debits</div>
            </div>
            <div class="kpi-icon soft-indigo">
              <i class="bi bi-wallet2"></i>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6 col-xl-3">
        <div class="kpi-card kpi-card--amber h-100">
          <div class="d-flex justify-content-between align-items-start gap-3">
            <div>
              <div class="kpi-label">Loans Outstanding</div>
              <div class="kpi-value">
                {{ currency(kpis.loans.outstanding_total) }}
              </div>
              <div class="kpi-note">Current unpaid loan total</div>
            </div>
            <div class="kpi-icon soft-amber">
              <i class="bi bi-bank"></i>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6 col-xl-3">
        <div class="kpi-card kpi-card--red h-100">
          <div class="d-flex justify-content-between align-items-start gap-3">
            <div>
              <div class="kpi-label">Pending Withdrawals</div>
              <div class="kpi-value">
                {{ fmt(kpis.withdrawals.pending_count) }}
              </div>
              <div class="kpi-note">Awaiting action</div>
            </div>
            <div class="kpi-icon soft-red">
              <i class="bi bi-cash-stack"></i>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Financial snapshot -->
    <div class="row g-3 mb-4">
      <div class="col-lg-8">
        <div class="card dashboard-card h-100">
          <div
            class="card-header bg-white d-flex justify-content-between align-items-center"
          >
            <strong>Financial Statement</strong>
            <span class="text-muted small"
              >{{ niceDate(filters.from) }} → {{ niceDate(filters.to) }}</span
            >
          </div>
          <div class="card-body">
            <div class="summary-grid">
              <div class="summary-item">
                <div class="small text-muted">Opening Balance In</div>
                <div class="summary-value">
                  {{ currency(period.opening_balance_in) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Contributions In</div>
                <div class="summary-value">
                  {{ currency(period.contributions_in) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Loan Repayment In</div>
                <div class="summary-value">
                  {{ currency(period.loan_repayment_cash_in) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Profit In</div>
                <div class="summary-value">
                  {{ currency(period.profit_in) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Penalty Cash In</div>
                <div class="summary-value">
                  {{ currency(period.penalty_cash_in) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Loan Disbursed Out</div>
                <div class="summary-value">
                  {{ currency(period.loan_disbursed_out) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Opening Loan Migrated</div>
                <div class="summary-value text-warning">
                  {{ currency(period.opening_loan_migrated) }}
                </div>
                <div class="small text-muted">Historical receivable</div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Withdrawals Out</div>
                <div class="summary-value">
                  {{ currency(period.withdrawals_out) }}
                </div>
              </div>

              <div class="summary-item summary-item--accent">
                <div class="small text-muted">Net Cash Flow</div>

                <div
                  class="summary-value"
                  :class="netFlow >= 0 ? 'text-success' : 'text-danger'"
                >
                  {{ netFlow >= 0 ? "+" : "-" }}
                  {{ fmt(Math.abs(netFlow)) }} RWF
                </div>

                <div class="small text-muted">
                  Excludes migrated opening loans
                </div>
              </div>
            </div>
            <div class="text-muted small mt-3">
              Net cash flow = (Opening balance + Contributions + Loan repayments
              + Profit + Penalty cash) − (Loan disbursed + Withdrawals)

              <br />

              <span class="text-warning">
                Opening loan migrated is shown separately because it is a
                brought-forward receivable, not cash movement.
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-4">
        <div class="card dashboard-card h-100">
          <div class="card-header bg-white">
            <strong>Attention Needed</strong>
          </div>

          <div class="card-body">
            <div class="health-list">
              <div class="health-row">
                <span class="text-muted small">Pending Loan Applications</span>
                <strong>{{ attention.pending_loan_applications }}</strong>
              </div>

              <div class="health-row">
                <span class="text-muted small">Pending Withdrawals</span>
                <strong class="text-danger">{{
                  attention.pending_withdrawals
                }}</strong>
              </div>

              <div class="health-row">
                <span class="text-muted small">Awaiting Withdrawals</span>
                <strong>{{ attention.approved_withdrawals }}</strong>
              </div>

              <div class="health-row">
                <span class="text-muted small">Queued for FY Close</span>
                <strong>{{ attention.queued_withdrawals }}</strong>
              </div>

              <div class="health-row">
                <span class="text-muted small">Overdue Loans</span>
                <strong
                  :class="
                    attention.overdue_loans > 0 ? 'text-danger' : 'text-success'
                  "
                >
                  {{ attention.overdue_loans }}
                </strong>
              </div>

              <div class="health-row">
                <span class="text-muted small">Unpaid Penalties</span>
                <strong
                  :class="
                    attention.unpaid_penalties > 0
                      ? 'text-danger'
                      : 'text-success'
                  "
                >
                  {{ attention.unpaid_penalties }}
                </strong>
              </div>

              <div class="health-row">
                <span class="text-muted small"
                  >Members with Unpaid Penalties</span
                >
                <strong>{{ attention.members_with_unpaid_penalties }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Portfolio + penalties -->
    <div class="row g-3 mb-4">
      <div class="col-lg-6">
        <div class="card dashboard-card h-100">
          <div class="card-header bg-white">
            <strong>Loan Portfolio</strong>
          </div>
          <div class="card-body">
            <div class="summary-grid summary-grid--compact">
              <div class="summary-item">
                <div class="small text-muted">Active Loans</div>
                <div class="summary-value">
                  {{ fmt(kpis.loans.active_count) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Defaulted Loans</div>
                <div class="summary-value">
                  {{ fmt(kpis.loans.defaulted_count) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Overdue Loans</div>
                <div class="summary-value">
                  {{ fmt(kpis.loans.overdue_count) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Outstanding Principal</div>
                <div class="summary-value">
                  {{ currency(kpis.loans.outstanding_principal) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Outstanding Interest</div>
                <div class="summary-value">
                  {{ currency(kpis.loans.outstanding_interest) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Pending Applications</div>
                <div class="summary-value">
                  {{ fmt(kpis.loans.pending_applications_count) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-6">
        <div class="card dashboard-card h-100">
          <div class="card-header bg-white">
            <strong>Penalty Overview</strong>
          </div>
          <div class="card-body">
            <div class="summary-grid summary-grid--compact">
              <div class="summary-item">
                <div class="small text-muted">Unpaid Count</div>
                <div class="summary-value">
                  {{ fmt(kpis.penalties.unpaid_count) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Unpaid Amount</div>
                <div class="summary-value text-danger">
                  {{ currency(kpis.penalties.unpaid_amount) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Affected Members</div>
                <div class="summary-value">
                  {{ fmt(kpis.penalties.members_with_unpaid_penalties) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Created This Period</div>
                <div class="summary-value">
                  {{ fmt(periodPenalties.created_count) }}
                </div>
                <div class="small text-muted mt-1">
                  {{ currency(periodPenalties.created_amount) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Paid This Period</div>
                <div class="summary-value">
                  {{ fmt(periodPenalties.paid_count) }}
                </div>
                <div class="small text-muted mt-1">
                  {{ currency(periodPenalties.paid_amount) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Waived This Period</div>
                <div class="summary-value">
                  {{ fmt(periodPenalties.waived_count) }}
                </div>
                <div class="small text-muted mt-1">
                  {{ currency(periodPenalties.waived_amount) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Recent activity -->
    <div class="card dashboard-card">
      <div
        class="card-header bg-white d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2"
      >
        <div>
          <strong>Recent Activity</strong>
          <div class="text-muted small">
            Latest 15 transactions across the system
          </div>
        </div>

        <span class="text-muted small">{{ recent.length }} record(s)</span>
      </div>

      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table dashboard-table align-middle mb-0">
            <thead>
              <tr>
                <th style="width: 16%">Date</th>
                <th style="width: 18%">Member</th>
                <th style="width: 15%">Direction</th>
                <th style="width: 18%">Type</th>
                <th>Reference</th>
                <th style="width: 16%" class="text-end">Amount</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="t in recent" :key="t.id">
                <td class="text-muted">{{ fmtDateTime(t.created_at) }}</td>

                <td>
                  <div class="fw-semibold text-dark">
                    {{ t.member_name || "-" }}
                  </div>
                  <div class="small text-muted">{{ t.member_email || "" }}</div>
                </td>

                <td>
                  <span
                    v-if="Number(t.credit) > 0"
                    class="badge text-bg-success"
                  >
                    <i class="bi bi-plus-circle me-1"></i>IN
                  </span>
                  <span v-else class="badge text-bg-danger">
                    <i class="bi bi-dash-circle me-1"></i>OUT
                  </span>
                </td>

                <td>
                  <span class="type-pill">{{ humanType(t.type) }}</span>
                </td>

                <td class="text-muted">{{ cleanReference(t.reference) }}</td>

                <td class="text-end fw-semibold">
                  <span v-if="Number(t.credit) > 0" class="text-success"
                    >+{{ fmt(t.credit) }}</span
                  >
                  <span v-else class="text-danger">-{{ fmt(t.debit) }}</span>
                  <span class="text-muted"> RWF</span>
                </td>
              </tr>

              <tr v-if="!loading && recent.length === 0">
                <td colspan="6" class="text-center text-muted py-4">
                  No activity yet
                </td>
              </tr>

              <tr v-if="loading">
                <td colspan="6" class="text-center text-muted py-4">
                  Loading...
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getDashboard } from "@/services/dashboard";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.css";

export default {
  name: "AdminDashboardView",

  data() {
    const today = new Date().toISOString().slice(0, 10);
    const y = new Date().getFullYear();
    const start = `${y}-01-01`;

    return {
      loading: false,
      error: "",
      filters: { from: start, to: today },

      kpis: this.defaultKpis(),
      cashflow: this.defaultCashflow(),
      attention: this.defaultAttention(),
      periodPenalties: this.defaultPeriodPenalties(),
      recent: [],

      fpFrom: null,
      fpTo: null,
    };
  },

  computed: {
    period() {
      return this.kpis?.period || {};
    },

    netFlow() {
      return Number(this.period.cash_net_flow || 0);
    },
  },

  mounted() {
    this.initDatePickers();
    this.load();
  },

  beforeUnmount() {
    this.fpFrom?.destroy?.();
    this.fpTo?.destroy?.();
    this.fpFrom = null;
    this.fpTo = null;
  },

  methods: {
    defaultKpis() {
      return {
        members: {
          total: 0,
        },
        balances: {
          system_net_balance: 0,
          opening_balances_total: 0,
        },
        loans: {
          active_count: 0,
          defaulted_count: 0,
          overdue_count: 0,
          outstanding_total: 0,
          outstanding_principal: 0,
          outstanding_interest: 0,
          pending_applications_count: 0,
        },
        penalties: {
          unpaid_count: 0,
          unpaid_amount: 0,
          members_with_unpaid_penalties: 0,
        },
        withdrawals: {
          pending_count: 0,
          approved_count: 0,
          queued_for_fy_close_count: 0,
          paid_count: 0,
          paid_total: 0,
        },
      };
    },

    defaultCashflow() {
      return {
        opening_balance_in: 0,
        contributions_in: 0,
        loan_repayment_in: 0,
        profit_in: 0,
        penalty_cash_in: 0,
        loan_disbursed_out: 0,
        opening_loan_migrated: 0,
        withdrawals_out: 0,
      };
    },

    defaultAttention() {
      return {
        pending_loan_applications: 0,
        pending_withdrawals: 0,
        approved_withdrawals: 0,
        queued_withdrawals: 0,
        overdue_loans: 0,
        unpaid_penalties: 0,
        members_with_unpaid_penalties: 0,
      };
    },

    defaultPeriodPenalties() {
      return {
        created_count: 0,
        created_amount: 0,
        paid_count: 0,
        paid_amount: 0,
        waived_count: 0,
        waived_amount: 0,
      };
    },

    fmt(n) {
      return Number(n || 0).toLocaleString();
    },

    currency(n) {
      return `${this.fmt(n)} RWF`;
    },

    niceDate(dt) {
      if (!dt) return "-";
      const d = new Date(dt);
      if (isNaN(d.getTime())) return String(dt);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    },

    fmtDateTime(dt) {
      if (!dt) return "-";
      const s = String(dt);
      return s.includes("T")
        ? s.replace("T", " ").slice(0, 19)
        : s.slice(0, 19);
    },

    humanType(type) {
      const labels = {
        opening_balance: "Opening Balance",
        opening_savings: "Opening Savings",
        opening_loan: "Opening Loan",
        opening_loan_reversal: "Opening Loan Reversal",
        opening_loan_undo: "Opening Loan Undo",
        contribution: "Contribution",
        contribution_reversal: "Contribution Reversal",
        contribution_undo: "Contribution Undo",
        profit: "Profit",
        profit_reversal: "Profit Reversal",
        profit_undo: "Profit Undo",
        loan_disbursement: "Loan Disbursement",
        loan_disbursement_reversal: "Loan Disbursement Reversal",
        loan_disbursement_undo: "Loan Disbursement Undo",
        loan_repayment: "Loan Repayment",
        loan_repayment_reversal: "Loan Repayment Reversal",
        loan_repayment_undo: "Loan Repayment Undo",
        penalty_paid: "Penalty Payment",
        penalty_paid_reversal: "Penalty Payment Reversal",
        penalty_paid_undo: "Penalty Payment Undo",
        member_withdrawal: "Member Withdrawal",
        member_exit_withdrawal: "Exit Withdrawal",
      };

      return labels[type] || (type ? String(type).replaceAll("_", " ") : "-");
    },

    cleanReference(reference) {
      if (!reference) return "-";
      return String(reference).replace(" - ", " — ");
    },

    initDatePickers() {
      const fromEl = this.$refs.fromInput;
      const toEl = this.$refs.toInput;

      if (fromEl) {
        this.fpFrom?.destroy?.();
        this.fpFrom = flatpickr(fromEl, {
          dateFormat: "Y-m-d",
          defaultDate: this.filters.from,
          disableMobile: true,
          allowInput: false,
          onChange: (_, dateStr) => {
            this.filters.from = dateStr;
            if (this.filters.to && dateStr > this.filters.to) {
              this.filters.to = dateStr;
              this.fpTo?.setDate?.(dateStr, true, "Y-m-d");
            }
          },
        });
      }

      if (toEl) {
        this.fpTo?.destroy?.();
        this.fpTo = flatpickr(toEl, {
          dateFormat: "Y-m-d",
          defaultDate: this.filters.to,
          disableMobile: true,
          allowInput: false,
          onChange: (_, dateStr) => {
            this.filters.to = dateStr;
            if (this.filters.from && dateStr < this.filters.from) {
              this.filters.from = dateStr;
              this.fpFrom?.setDate?.(dateStr, true, "Y-m-d");
            }
          },
        });
      }
    },

    setThisYear() {
      const y = new Date().getFullYear();
      const start = `${y}-01-01`;
      const today = new Date().toISOString().slice(0, 10);

      this.filters.from = start;
      this.filters.to = today;

      this.fpFrom?.setDate?.(start, true, "Y-m-d");
      this.fpTo?.setDate?.(today, true, "Y-m-d");

      this.load();
    },

    setLast30Days() {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 30);

      const from = start.toISOString().slice(0, 10);
      const to = end.toISOString().slice(0, 10);

      this.filters.from = from;
      this.filters.to = to;

      this.fpFrom?.setDate?.(from, true, "Y-m-d");
      this.fpTo?.setDate?.(to, true, "Y-m-d");

      this.load();
    },

    async load() {
      this.error = "";
      this.loading = true;

      try {
        const res = await getDashboard(this.filters);
        const payload = res?.data || {};

        this.kpis = {
          ...this.defaultKpis(),
          ...(payload?.kpis || {}),
          members: {
            ...this.defaultKpis().members,
            ...(payload?.kpis?.members || {}),
          },
          balances: {
            ...this.defaultKpis().balances,
            ...(payload?.kpis?.balances || {}),
          },
          loans: {
            ...this.defaultKpis().loans,
            ...(payload?.kpis?.loans || {}),
          },
          penalties: {
            ...this.defaultKpis().penalties,
            ...(payload?.kpis?.penalties || {}),
          },
          withdrawals: {
            ...this.defaultKpis().withdrawals,
            ...(payload?.kpis?.withdrawals || {}),
          },
        };

        const period = payload?.kpis?.period || {};

        this.cashflow = {
          opening_balance_in: period.opening_balance_in || 0,
          contributions_in: period.contributions_in || 0,
          loan_repayment_in: period.loan_repayment_cash_in || 0,
          profit_in: period.profit_in || 0,
          penalty_cash_in: period.penalty_cash_in || 0,
          loan_disbursed_out: period.loan_disbursed_out || 0,
          opening_loan_migrated: period.opening_loan_migrated || 0,
          withdrawals_out: period.withdrawals_out || 0,
        };

        this.attention = {
          pending_loan_applications:
            payload?.kpis?.loans?.pending_applications_count || 0,

          pending_withdrawals: payload?.kpis?.withdrawals?.pending_count || 0,

          approved_withdrawals: payload?.kpis?.withdrawals?.approved_count || 0,

          queued_withdrawals:
            payload?.kpis?.withdrawals?.queued_for_fy_close_count || 0,

          overdue_loans: payload?.kpis?.loans?.overdue_count || 0,

          unpaid_penalties: payload?.kpis?.penalties?.unpaid_count || 0,

          members_with_unpaid_penalties:
            payload?.kpis?.penalties?.members_with_unpaid_penalties || 0,
        };

        this.periodPenalties = {
          created_count: period.penalties_created_count || 0,
          created_amount: period.penalties_created_amount || 0,
          paid_count: period.penalties_paid_count || 0,
          paid_amount: period.penalties_paid_amount || 0,
          waived_count: period.penalties_waived_count || 0,
          waived_amount: period.penalties_waived_amount || 0,
        };

        this.recent = Array.isArray(payload?.recent_transactions)
          ? payload.recent_transactions
          : [];
      } catch (e) {
        this.error =
          e?.response?.data?.message ||
          e?.message ||
          "Failed to load dashboard";
        this.kpis = this.defaultKpis();
        this.cashflow = this.defaultCashflow();
        this.attention = this.defaultAttention();
        this.periodPenalties = this.defaultPeriodPenalties();
        this.recent = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.admin-dashboard-view {
  color: #0f172a;
}

.dashboard-hero {
  padding: 24px;
  border-radius: 26px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.14),
    rgba(16, 185, 129, 0.1)
  );
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.05);
}

.hero-eyebrow {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #2563eb;
  font-weight: 700;
  margin-bottom: 6px;
}

.hero-title {
  font-weight: 800;
  color: #0f172a;
}

.hero-text {
  color: #475569;
  max-width: 760px;
}

.hero-pill {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid #e2e8f0;
  color: #475569;
  font-size: 0.9rem;
}

.hero-filter-card {
  width: 100%;
  max-width: 520px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 14px;
}

.quick-btn {
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  background: rgba(255, 255, 255, 0.9);
}

.dashboard-card {
  border: 0;
  border-radius: 22px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
  overflow: hidden;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.dashboard-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
}

.kpi-card {
  position: relative;
  padding: 22px;
  border-radius: 22px;
  background: linear-gradient(180deg, #ffffff, #f8fafc);
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;
}

.kpi-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 20px 36px rgba(15, 23, 42, 0.08);
}

.kpi-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  border-top-left-radius: 22px;
  border-top-right-radius: 22px;
}

.kpi-card--blue::before {
  background: linear-gradient(90deg, #2563eb, #38bdf8);
}

.kpi-card--indigo::before {
  background: linear-gradient(90deg, #4f46e5, #2563eb);
}

.kpi-card--amber::before {
  background: linear-gradient(90deg, #f59e0b, #fbbf24);
}

.kpi-card--red::before {
  background: linear-gradient(90deg, #ef4444, #f97316);
}

.kpi-label {
  font-size: 0.92rem;
  color: #64748b;
  margin-bottom: 8px;
}

.kpi-value {
  font-size: clamp(1.05rem, 1.35vw, 1.5rem);
  font-weight: 800;
  line-height: 1.25;
  color: #0f172a;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.kpi-note {
  margin-top: 8px;
  font-size: 0.9rem;
  color: #64748b;
}

.kpi-icon {
  width: 54px;
  height: 54px;
  min-width: 54px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.soft-blue {
  background: rgba(37, 99, 235, 0.12);
  color: #2563eb;
}

.soft-indigo {
  background: rgba(79, 70, 229, 0.12);
  color: #4f46e5;
}

.soft-amber {
  background: rgba(245, 158, 11, 0.14);
  color: #d97706;
}

.soft-red {
  background: rgba(220, 38, 38, 0.12);
  color: #dc2626;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
}

.summary-grid--compact {
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
}

.summary-item {
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff, #fbfdff);
}

.summary-item--accent {
  box-shadow: inset 0 0 0 1px rgba(59, 130, 246, 0.08);
}

.summary-value {
  font-weight: 700;
  color: #0f172a;
}

.health-list {
  display: grid;
  gap: 12px;
}

.health-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #eef2f7;
  border-radius: 16px;
  background: #ffffff;
}

.table-responsive {
  border-radius: 20px;
  overflow: hidden;
}

.dashboard-table thead {
  background: rgba(59, 130, 246, 0.08);
}

.dashboard-table thead th {
  border-bottom: 0;
  color: #1e40af;
  font-weight: 600;
}

.dashboard-table tbody tr:hover {
  background: rgba(15, 23, 42, 0.04);
}

.type-pill {
  display: inline-block;
  padding: 7px 12px;
  border-radius: 999px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
}

.form-control,
.btn {
  border-radius: 12px;
}
.kpi-card .d-flex {
  min-width: 0;
}

.kpi-card .d-flex > div:first-child {
  min-width: 0;
  flex: 1;
}

@media (max-width: 767.98px) {
  .dashboard-hero,
  .kpi-card,
  .dashboard-card {
    border-radius: 18px;
  }

  .kpi-value {
    font-size: 1.15rem;
  }
  .kpi-icon {
    width: 48px;
    height: 48px;
    min-width: 48px;
  }
}
</style>
